if (!customElements.get('media-gallery')) {
  class MediaGallery extends HTMLElement {
    constructor() {
      super();

      if (Shopify.designMode) {
        setTimeout(() => this.init(), 200);
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('on:debounced-resize', this.resizeHandler);

      if (this.resizeInitHandler) {
        window.removeEventListener('on:debounced-resize', this.resizeInitHandler);
      }

      if (this.zoomInitHandler) {
        window.removeEventListener('on:debounced-resize', this.zoomInitHandler);
      }
    }

    init() {
      this.section = this.closest('.js-product');
      this.noSelectedVariant = this.hasAttribute('data-no-selected-variant');
      this.mediaGroupingEnabled = this.hasAttribute('data-media-grouping-enabled')
        && this.getMediaGroupData();
      this.stackedScroll = this.dataset.stackedScroll;
      this.stackedUnderline = this.dataset.stackedUnderline === 'true' && !this.mediaGroupingEnabled;
      this.isFeatured = this.dataset.isFeatured === 'true';
      this.viewer = this.querySelector('.media-viewer');
      this.thumbs = this.querySelector('.media-thumbs');
      this.thumbsItems = this.querySelectorAll('.media-thumbs__item');
      this.controls = this.querySelector('.media-ctrl');
      this.prevBtn = this.querySelector('.media-ctrl__btn[name="prev"]');
      this.nextBtn = this.querySelector('.media-ctrl__btn[name="next"]');
      this.counterCurrent = this.querySelector('.media-ctrl__current-item');
      this.counterTotal = this.querySelector('.media-ctrl__total-items');
      this.liveRegion = this.querySelector('.media-gallery__status');
      this.zoomLinks = this.querySelectorAll('.js-zoom-link');
      this.loadingSpinner = this.querySelector('.loading-spinner');
      this.xrButton = this.querySelector('.media-xr-button');

      if (this.hasAttribute('data-zoom-enabled')) {
        this.galleryModal = this.querySelector('.js-media-zoom-template').content.firstElementChild.cloneNode(true);
      }

      if (this.mediaGroupingEnabled && !this.noSelectedVariant) {
        this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
      }

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        this.resizeInitHandler = this.resizeInitHandler || this.initGallery.bind(this);
        window.addEventListener('on:debounced-resize', this.resizeInitHandler);
        this.setVisibleItems();
        this.previousMediaItem = this.querySelector('.media-viewer__item.is-current-variant');
        setTimeout(() => this.customSetActiveMedia(this.previousMediaItem, this.stackedScroll === 'always'), 200);
      } else {
        this.initGallery();
      }

      if (this.zoomLinks) {
        if (this.dataset.zoomTrigger === 'hover') {
          this.triggerZoomInit();
        } else {
          this.zoomLinks.forEach((zoomLink) => {
            zoomLink.addEventListener('click', (evt) => {
              this.triggerZoomInit();
              evt.preventDefault();
            });
          });
        }
      }

      this.section.addEventListener('on:variant:change', this.onVariantChange.bind(this));
    }

    triggerZoomInit() {
      this.zoomInitHandler = this.zoomInitHandler || this.initZoom.bind(this);
      this.zoomEventListener = this.zoomEventListener || this.handleZoomMouseMove.bind(this);
      window.addEventListener('on:debounced-resize', this.zoomInitHandler);
      this.initZoom();
      this.zoomLinks.forEach((zoomLink) => {
        zoomLink.addEventListener('click', (evt) => {
          evt.preventDefault();
        });
      });
    }

    /**
     * Handle a change in variant on the page.
     * @param {Event} evt - variant change event dispatched by variant-picker
     */
    onVariantChange(evt) {
      if (this.mediaGroupingEnabled) {
        this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
      }

      if (evt.detail.variant && evt.detail.variant.featured_media) {
        const variantMedia = this.viewer.querySelector(
          `[data-media-id="${evt.detail.variant.featured_media.id}"]`
        );
        this.customSetActiveMedia(variantMedia, true);
      }
    }

    /**
     * Gets the media group from currently selected variant options.
     * @returns {?object}
     */
    getMediaGroupFromOptionSelectors() {
      const optionSelectors = this.section.querySelectorAll('.option-selector');
      if (optionSelectors.length > this.getMediaGroupData().groupOptionIndex) {
        const selector = optionSelectors[this.getMediaGroupData().groupOptionIndex];
        if (selector.dataset.selectorType === 'dropdown') {
          return selector.querySelector('.custom-select__btn').textContent.trim();
        }
        return selector.querySelector('input:checked').value;
      }
      return null;
    }

    /**
     * Gets the variant media associations for a product.
     * @returns {?object}
     */
    getMediaGroupData() {
      if (typeof this.variantMediaData === 'undefined') {
        const dataEl = this.querySelector('.js-data-variant-media');
        this.variantMediaData = dataEl ? JSON.parse(dataEl.textContent) : false;
      }

      return this.variantMediaData;
    }

    /**
     * Gets an object mapping media to groups, and the reverse
     * @returns {?object}
     */
    getMediaGroupMap() {
      if (!this.mediaGroupMap) {
        this.mediaGroupMap = {
          groups: {}
        };

        // set up grouping
        const variantMediaData = this.getMediaGroupData();
        let currentMediaOptionName = false;
        this.viewerItems = this.querySelectorAll('.media-viewer__item');
        this.viewerItems.forEach((item) => {
          for (let i = 0; i < variantMediaData.variantMedia.length; i += 1) {
            if (parseInt(item.dataset.mediaId, 10) === variantMediaData.variantMedia[i].mediaId) {
              if (currentMediaOptionName !== variantMediaData.variantMedia[i].option) {
                currentMediaOptionName = variantMediaData.variantMedia[i].option;
              }
            }
          }
          if (currentMediaOptionName) {
            if (!this.mediaGroupMap.groups[currentMediaOptionName]) {
              this.mediaGroupMap.groups[currentMediaOptionName] = {
                name: currentMediaOptionName,
                items: []
              };
            }
            const groupItem = { main: item };
            if (this.thumbs) {
              groupItem.thumb = this.thumbs.querySelector(
                `[data-media-id="${item.dataset.mediaId}"].media-thumbs__item`
              );
            }
            this.mediaGroupMap.groups[currentMediaOptionName].items.push(groupItem);
          }
        });

        // add helper
        this.mediaGroupMap.groupFromItem = (item) => {
          const groups = Object.keys(this.mediaGroupMap.groups);
          for (let i = 0; i < groups; i += 1) {
            const group = groups[i];
            for (let j = 0; j < this.mediaGroupMap.groups[group].items.length; j += 1) {
              if (this.mediaGroupMap.groups[group].items[j] === item) {
                return this.mediaGroupMap.groups[group];
              }
            }
          }
          return this.mediaGroupMap.groups[Object.keys(this.mediaGroupMap.groups)[0]];
        };
      }

      return this.mediaGroupMap;
    }

    /**
     * Show only images associated to the current variant
     * @param {string} groupName - optional - Group to show (uses this.currentItem if empty)
     */
    setActiveMediaGroup(groupName) {
      const mediaGroupMap = this.getMediaGroupMap();
      const selectedGroup = groupName
        ? mediaGroupMap.groups[groupName]
        : mediaGroupMap.groupFromItem(this.currentItem);

      if (selectedGroup) {
        if (this.currentGroup !== selectedGroup) {
          this.currentGroup = selectedGroup;
          this.viewerItems.forEach((item) => {
            item.style.display = 'none';
            item.classList.remove('media-viewer__item--single');
          });
          this.thumbsItems.forEach((item) => {
            item.style.display = 'none';
          });

          let currentItemIsVisible = false;
          selectedGroup.items.forEach((item) => {
            item.main.style.display = '';
            if (item.thumb) {
              item.thumb.style.display = '';
            }
            if (item.main === this.currentItem) {
              currentItemIsVisible = true;
            }
          });
          this.setVisibleItems();

          // If current item is not in this group, set it as the active item
          if (!currentItemIsVisible) {
            this.customSetActiveMedia(selectedGroup.items[0].main, true);
          }

          // Handle single images on stacked view
          if (selectedGroup.items.length === 1) {
            selectedGroup.items[0].main.classList.add('media-viewer__item--single');
          }
        }
      } else {
        this.viewerItems.forEach((item) => {
          item.style.display = '';
        });
        this.thumbsItems.forEach((item) => {
          item.style.display = '';
        });
      }
    }

    /**
     * Initialises the media gallery slider and associated controls.
     */
    initGallery() {
      this.setVisibleItems();
      if (this.visibleItems.length <= 1) return;

      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;
      this.currentIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);
      this.currentItem = this.visibleItems[this.currentIndex];
      this.addListeners();

      if (this.thumbs && this.currentItem) {
        this.currentThumb = this.thumbs.querySelector(
          `[data-media-id="${this.currentItem.dataset.mediaId}"]`
        );
      }

      if (!this.isFeatured && document.hasFocus()) {
        // Eager load the slider images for smooth UX
        this.viewer.querySelectorAll('.product-image[loading="lazy"]').forEach((img, index) => {
          setTimeout(() => {
            img.loading = 'eager';
          }, 500 * (index + 1));
        });
      }

      const currentItem = this.querySelector('.media-viewer__item.is-current-variant');
      this.customSetActiveMedia(currentItem, true);
    }

    addListeners() {
      this.viewer.addEventListener('scroll', this.handleScroll.bind(this));
      if (this.controls) this.controls.addEventListener('click', this.handleNavClick.bind(this));
      if (this.thumbs) this.thumbs.addEventListener('click', this.handleThumbClick.bind(this));
      
      // Add click handler for mobile fullscreen on media items (using event delegation)
      if (!theme.mediaMatches.md) {
        this.viewer.addEventListener('click', this.handleMediaItemClick.bind(this));
      }
      
      this.resizeHandler = this.resizeHandler || this.handleResize.bind(this);
      window.addEventListener('on:debounced-resize', this.resizeHandler);
    }

    /**
     * Initialized the zoom on hover for desktop
     */
    initZoom() {
      this.zoomLinks.forEach((el) => {
        const zoomWidth = Number(el.querySelector('.zoom-image').dataset.originalWidth || 0);
        const imageWidth = el.querySelector('.product-image').getBoundingClientRect().width;
        if (theme.mediaMatches.md && ((zoomWidth - 75) > (imageWidth))) {
          el.addEventListener('mousemove', this.zoomEventListener);
          el.classList.remove('pointer-events-none');
        } else {
          el.removeEventListener('mousemove', this.zoomEventListener);
          el.classList.add('pointer-events-none');
        }
      });
    }

    /**
     * Handles mouse move over a zoomable image
     * @param {?object} evt - Event object.
     */
    handleZoomMouseMove(evt) {
      const hoverElem = evt.currentTarget;
      const zoomImage = hoverElem.querySelector('.js-zoom-image');

      // Download the zoom image if necessary
      if (zoomImage.dataset.src) {
        this.loadingSpinner.classList.remove('loading-spinner--out');

        const img = new Image();
        img.src = zoomImage.dataset.src;
        img.onload = () => {
          zoomImage.src = img.src;
          hoverElem.classList.remove('media--zoom-not-loaded');
          this.loadingSpinner.classList.add('loading-spinner--out');
        };
        zoomImage.removeAttribute('data-src');
      }

      try {
        const offsetX = evt.offsetX ? evt.offsetX : evt.touches[0].pageX;
        const offsetY = evt.offsetY ? evt.offsetY : evt.touches[0].pageY;
        const x = (offsetX / zoomImage.offsetWidth) * 100;
        const y = (offsetY / zoomImage.offsetHeight) * 100;
        zoomImage.style.objectPosition = `${x}% ${y}%`;
      } catch (err) {
        // Fail silently
      }
    }

    /**
     * Handles 'scroll' events on the main media container.
     */
    handleScroll() {
      const newIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);

      if (newIndex !== this.currentIndex) {
        const viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;

        // If scroll wasn't caused by a resize event, update the active media.
        if (viewerItemOffset === this.viewerItemOffset) {
          this.customSetActiveMedia(this.visibleItems[newIndex], false);
        }
      }
    }

    /**
     * Handles 'click' events on the controls container.
     * @param {object} evt - Event object.
     */
    handleNavClick(evt) {
      if (!evt.target.matches('.media-ctrl__btn')) return;

      const itemToShow = evt.target === this.nextBtn
        ? this.visibleItems[this.currentIndex + 1]
        : this.visibleItems[this.currentIndex - 1];

      this.viewer.scrollTo({ left: itemToShow.offsetLeft, behavior: 'smooth' });
    }

    /**
     * Handles 'click' events on the thumbnails container.
     * @param {object} evt - Event object.
     */
    handleThumbClick(evt) {
      const thumb = evt.target.closest('[data-media-id]');
      if (!thumb) return;

      const itemToShow = this.querySelector(`[data-media-id="${thumb.dataset.mediaId}"]`);
      this.customSetActiveMedia(itemToShow);

      MediaGallery.playActiveMedia(itemToShow);
    }

    /**
     * Handles 'click' events on media viewer items for mobile fullscreen slider.
     * @param {object} evt - Event object.
     */
    handleMediaItemClick(evt) {
      // Only handle on mobile devices
      if (theme.mediaMatches.md) return;
      
      // Don't trigger if clicking on controls or interactive elements
      if (evt.target.closest('.media-ctrl, .media-poster__btn, .js-zoom-link')) return;
      
      // Find the media item that was clicked
      const clickedMediaItem = evt.target.closest('.media-viewer__item');
      if (!clickedMediaItem) return;
      
      // Get all visible media items and their images
      this.setVisibleItems();
      const visibleItems = Array.from(this.visibleItems);
      const clickedIndex = visibleItems.indexOf(clickedMediaItem);
      
      if (clickedIndex === -1) return;
      
      // Create Amazon-style popup slider
      this.openMobileSlider(visibleItems, clickedIndex);
    }

    /**
     * Opens the mobile slider popup with all images.
     * @param {Array} mediaItems - Array of visible media items.
     * @param {number} startIndex - Index of the media item to start with (in mediaItems array).
     */
    openMobileSlider(mediaItems, startIndex) {
      // Create modal overlay
      const modal = document.createElement('div');
      modal.className = 'mobile-image-slider';
      modal.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(255, 255, 255, 0.98);
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
        z-index: 10000;
        display: flex;
        flex-direction: column;
        touch-action: pan-y;
      `;
      
      // Create header with close button and counter
      const header = document.createElement('div');
      header.style.cssText = `
        position: relative;
        padding: 16px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        z-index: 10001;
      `;
      
      const counter = document.createElement('div');
      counter.className = 'mobile-slider-counter';
      counter.style.cssText = `
        color: #333;
        font-size: 14px;
        font-weight: 500;
      `;
      
      const closeBtn = document.createElement('button');
      closeBtn.innerHTML = `
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15 5L5 15M5 5L15 15" stroke="#333" stroke-width="2" stroke-linecap="round"/>
        </svg>
      `;
      closeBtn.style.cssText = `
        background: #fff;
        border: 1px solid rgba(0, 0, 0, 0.1);
        color: #333;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        font-size: 20px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      `;
      
      header.appendChild(counter);
      header.appendChild(closeBtn);
      
      // Create slider container
      const sliderContainer = document.createElement('div');
      sliderContainer.className = 'mobile-slider-container';
      sliderContainer.style.cssText = `
        flex: 1;
        position: relative;
        overflow: hidden;
        display: flex;
        align-items: center;
      `;
      
      // Create image wrapper
      const imageWrapper = document.createElement('div');
      imageWrapper.className = 'mobile-slider-images';
      imageWrapper.style.cssText = `
        display: flex;
        width: 100%;
        height: 100%;
        transition: transform 0.3s ease-out;
        will-change: transform;
      `;
      
      // Create images - filter out items without images first
      // Store reference to original mediaItem for index mapping
      const images = [];
      const mediaItemToImageIndex = new Map();
      
      mediaItems.forEach((mediaItem, mediaItemIndex) => {
        const img = mediaItem.querySelector('img.product-image, img.zoom-image');
        if (!img || !img.src) return;
        
        const imageIndex = images.length; // This is the index in the images array
        mediaItemToImageIndex.set(mediaItem, imageIndex);
        
        const imageContainer = document.createElement('div');
        imageContainer.style.cssText = `
          flex: 0 0 100%;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 60px 16px 80px;
          box-sizing: border-box;
        `;
        
        const imageEl = document.createElement('img');
        imageEl.src = img.src;
        imageEl.alt = img.alt || '';
        imageEl.style.cssText = `
          max-width: 100%;
          max-height: 100%;
          object-fit: contain;
          user-select: none;
          -webkit-user-select: none;
        `;
        
        imageContainer.appendChild(imageEl);
        imageWrapper.appendChild(imageContainer);
        images.push({ container: imageContainer, element: imageEl, mediaItem });
      });
      
      // Find the correct start index in the images array
      const clickedMediaItem = mediaItems[startIndex];
      const actualStartIndex = mediaItemToImageIndex.get(clickedMediaItem) || 0;
      
      sliderContainer.appendChild(imageWrapper);
      
      // Create navigation arrows with SVG icons
      const prevArrow = document.createElement('button');
      prevArrow.innerHTML = `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15 18L9 12L15 6" stroke="#333" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `;
      prevArrow.className = 'mobile-slider-arrow mobile-slider-arrow--prev';
      prevArrow.type = 'button';
      prevArrow.setAttribute('aria-label', 'Previous image');
      prevArrow.style.cssText = `
        position: absolute;
        left: 16px;
        top: 50%;
        transform: translateY(-50%);
        background: #fff;
        border: 1px solid rgba(0, 0, 0, 0.1);
        color: #333;
        width: 44px;
        height: 44px;
        border-radius: 50%;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10002;
        padding: 0;
        opacity: 1;
        pointer-events: auto;
        touch-action: manipulation;
        -webkit-tap-highlight-color: transparent;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        transition: all 0.2s ease;
      `;
      
      const nextArrow = document.createElement('button');
      nextArrow.innerHTML = `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 18L15 12L9 6" stroke="#333" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `;
      nextArrow.className = 'mobile-slider-arrow mobile-slider-arrow--next';
      nextArrow.type = 'button';
      nextArrow.setAttribute('aria-label', 'Next image');
      nextArrow.style.cssText = `
        position: absolute;
        right: 16px;
        top: 50%;
        transform: translateY(-50%);
        background: #fff;
        border: 1px solid rgba(0, 0, 0, 0.1);
        color: #333;
        width: 44px;
        height: 44px;
        border-radius: 50%;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10002;
        padding: 0;
        opacity: 1;
        pointer-events: auto;
        touch-action: manipulation;
        -webkit-tap-highlight-color: transparent;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        transition: all 0.2s ease;
      `;
      
      sliderContainer.appendChild(prevArrow);
      sliderContainer.appendChild(nextArrow);
      
      // Create thumbnail strip at bottom
      const thumbnailStrip = document.createElement('div');
      thumbnailStrip.className = 'mobile-slider-thumbnails';
      thumbnailStrip.style.cssText = `
        position: relative;
        width: 100%;
        padding: 12px 16px;
        background: rgba(255, 255, 255, 0.9);
        border-top: 1px solid rgba(0, 0, 0, 0.1);
        overflow-x: auto;
        overflow-y: hidden;
        display: flex;
        gap: 8px;
        scroll-behavior: smooth;
        scrollbar-width: none;
        -ms-overflow-style: none;
      `;
      
      // Hide scrollbar
      const style = document.createElement('style');
      style.textContent = `
        .mobile-slider-thumbnails::-webkit-scrollbar {
          display: none;
        }
      `;
      document.head.appendChild(style);
      
      const thumbnailWrapper = document.createElement('div');
      thumbnailWrapper.style.cssText = `
        display: flex;
        gap: 8px;
        min-width: max-content;
      `;
      
      const thumbnails = [];
      images.forEach((imgData, index) => {
        const thumbContainer = document.createElement('div');
        thumbContainer.className = 'mobile-slider-thumb';
        thumbContainer.dataset.index = index;
        thumbContainer.style.cssText = `
          flex: 0 0 60px;
          width: 60px;
          height: 60px;
          border-radius: 4px;
          overflow: hidden;
          border: 2px solid transparent;
          cursor: pointer;
          transition: border-color 0.2s, opacity 0.2s;
          background: rgba(255, 255, 255, 0.1);
        `;
        
        const thumbImg = document.createElement('img');
        thumbImg.src = imgData.element.src;
        thumbImg.style.cssText = `
          width: 100%;
          height: 100%;
          object-fit: cover;
          pointer-events: none;
        `;
        
        thumbContainer.appendChild(thumbImg);
        thumbnailWrapper.appendChild(thumbContainer);
        thumbnails.push(thumbContainer);
        
        // Click handler for thumbnail
        thumbContainer.addEventListener('click', () => {
          updateSlider(index);
          scrollThumbnailIntoView(index);
        });
      });
      
      thumbnailStrip.appendChild(thumbnailWrapper);
      
      // Function to scroll thumbnail into view
      const scrollThumbnailIntoView = (index) => {
        const thumb = thumbnails[index];
        if (thumb) {
          const thumbRect = thumb.getBoundingClientRect();
          const stripRect = thumbnailStrip.getBoundingClientRect();
          const scrollLeft = thumbnailStrip.scrollLeft;
          
          if (thumbRect.left < stripRect.left) {
            thumbnailStrip.scrollLeft = scrollLeft - (stripRect.left - thumbRect.left) - 8;
          } else if (thumbRect.right > stripRect.right) {
            thumbnailStrip.scrollLeft = scrollLeft + (thumbRect.right - stripRect.right) + 8;
          }
        }
      };
      
      modal.appendChild(header);
      modal.appendChild(sliderContainer);
      modal.appendChild(thumbnailStrip);
      document.body.appendChild(modal);
      
      // Slider state - use actualStartIndex from images array
      let currentIndex = actualStartIndex;
      let touchStartX = 0;
      let touchStartY = 0;
      let isDragging = false;
      let startTranslate = 0;
      let currentTranslate = 0;
      let animationId = 0;
      
      // Update slider position
      const updateSlider = (index, animate = true) => {
        // Clamp index to valid range
        if (index < 0) {
          index = 0;
        }
        if (index >= images.length) {
          index = images.length - 1;
        }
        
        // Update current index
        currentIndex = index;
        const translateX = -currentIndex * 100;
        
        // Apply transition
        if (animate) {
          imageWrapper.style.transition = 'transform 0.3s ease-out';
        } else {
          imageWrapper.style.transition = 'none';
        }
        
        // Update transform
        imageWrapper.style.transform = `translateX(${translateX}%)`;
        
        // Update counter
        if (counter) {
          counter.textContent = `${currentIndex + 1} / ${images.length}`;
        }
        
        // Update arrow visibility and disabled state
        if (prevArrow) {
          const isFirst = currentIndex === 0;
          prevArrow.style.opacity = isFirst ? '0.4' : '1';
          prevArrow.disabled = isFirst;
          prevArrow.style.pointerEvents = isFirst ? 'none' : 'auto';
          prevArrow.style.cursor = isFirst ? 'not-allowed' : 'pointer';
          prevArrow.style.background = isFirst ? '#f5f5f5' : '#fff';
          // Update SVG stroke color
          const prevSvg = prevArrow.querySelector('svg path');
          if (prevSvg) {
            prevSvg.setAttribute('stroke', isFirst ? '#999' : '#333');
          }
        }
        if (nextArrow) {
          const isLast = currentIndex === images.length - 1;
          nextArrow.style.opacity = isLast ? '0.4' : '1';
          nextArrow.disabled = isLast;
          nextArrow.style.pointerEvents = isLast ? 'none' : 'auto';
          nextArrow.style.cursor = isLast ? 'not-allowed' : 'pointer';
          nextArrow.style.background = isLast ? '#f5f5f5' : '#fff';
          // Update SVG stroke color
          const nextSvg = nextArrow.querySelector('svg path');
          if (nextSvg) {
            nextSvg.setAttribute('stroke', isLast ? '#999' : '#333');
          }
        }
        
        // Update thumbnail active state
        if (thumbnails && thumbnails.length > 0) {
          thumbnails.forEach((thumb, idx) => {
            if (thumb) {
              if (idx === currentIndex) {
                thumb.style.borderColor = '#fff';
                thumb.style.opacity = '1';
              } else {
                thumb.style.borderColor = 'transparent';
                thumb.style.opacity = '0.6';
              }
            }
          });
        }
        
        // Scroll thumbnail into view
        if (typeof scrollThumbnailIntoView === 'function') {
          scrollThumbnailIntoView(currentIndex);
        }
      };
      
      // Navigation functions - direct and simple
      const navigatePrev = () => {
        const targetIndex = currentIndex - 1;
        if (targetIndex >= 0) {
          updateSlider(targetIndex);
        }
      };
      
      const navigateNext = () => {
        const targetIndex = currentIndex + 1;
        if (targetIndex < images.length) {
          updateSlider(targetIndex);
        }
      };
      
      // Touch event handlers for swipe
      const handleTouchStart = (e) => {
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
        startTranslate = -currentIndex * 100;
        isDragging = true;
        imageWrapper.style.transition = 'none';
      };
      
      const handleTouchMove = (e) => {
        if (!isDragging) return;
        
        const currentX = e.touches[0].clientX;
        const currentY = e.touches[0].clientY;
        const diffX = currentX - touchStartX;
        const diffY = Math.abs(currentY - touchStartY);
        
        // Only allow horizontal swipe if horizontal movement is greater
        if (Math.abs(diffX) > diffY) {
          e.preventDefault();
          const windowWidth = window.innerWidth;
          currentTranslate = startTranslate + (diffX / windowWidth) * 100;
          
          // Limit translation
          const minTranslate = -(images.length - 1) * 100;
          const maxTranslate = 0;
          currentTranslate = Math.max(minTranslate, Math.min(maxTranslate, currentTranslate));
          
          imageWrapper.style.transform = `translateX(${currentTranslate}%)`;
        }
      };
      
      const handleTouchEnd = () => {
        if (!isDragging) return;
        isDragging = false;
        
        const threshold = 10; // Minimum swipe distance in percentage
        const diff = startTranslate - currentTranslate;
        
        if (Math.abs(diff) > threshold) {
          if (diff > 0 && currentIndex < images.length - 1) {
            navigateNext();
          } else if (diff < 0 && currentIndex > 0) {
            navigatePrev();
          } else {
            updateSlider(currentIndex);
          }
        } else {
          updateSlider(currentIndex);
        }
      };
      
      // Event listeners
      sliderContainer.addEventListener('touchstart', handleTouchStart, { passive: false });
      sliderContainer.addEventListener('touchmove', handleTouchMove, { passive: false });
      sliderContainer.addEventListener('touchend', handleTouchEnd);
      
      // Button handlers - using multiple event types for better compatibility
      const handlePrevClick = (e) => {
        if (e) {
          e.stopPropagation();
          e.preventDefault();
        }
        navigatePrev();
        return false;
      };
      
      const handleNextClick = (e) => {
        if (e) {
          e.stopPropagation();
          e.preventDefault();
        }
        navigateNext();
        return false;
      };
      
      // Attach multiple event types for better mobile support
      prevArrow.onclick = handlePrevClick;
      prevArrow.addEventListener('click', handlePrevClick, { passive: false, capture: false });
      prevArrow.addEventListener('touchend', handlePrevClick, { passive: false, capture: false });
      
      nextArrow.onclick = handleNextClick;
      nextArrow.addEventListener('click', handleNextClick, { passive: false, capture: false });
      nextArrow.addEventListener('touchend', handleNextClick, { passive: false, capture: false });
      
      const closeSlider = () => {
        modal.style.opacity = '0';
        modal.style.transition = 'opacity 0.2s';
        setTimeout(() => {
          if (modal.parentElement) {
            modal.remove();
          }
          document.body.style.overflow = '';
        }, 200);
      };
      
      closeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        closeSlider();
      });
      
      // Only close when clicking the modal background, not on slider container or images
      modal.addEventListener('click', (e) => {
        // Only close if clicking directly on the modal background (not on any children)
        if (e.target === modal) {
          closeSlider();
        }
      });
      
      // Prevent clicks on slider container and its children from closing the modal
      sliderContainer.addEventListener('click', (e) => {
        e.stopPropagation();
      });
      
      // Prevent clicks on header from closing the modal (except close button)
      header.addEventListener('click', (e) => {
        if (e.target !== closeBtn) {
          e.stopPropagation();
        }
      });
      
      // Prevent clicks on thumbnail strip from closing the modal
      thumbnailStrip.addEventListener('click', (e) => {
        e.stopPropagation();
      });
      
      const handleEscape = (e) => {
        if (e.key === 'Escape') {
          closeSlider();
          document.removeEventListener('keydown', handleEscape);
        }
      };
      document.addEventListener('keydown', handleEscape);
      
      // Initialize with the correct index in images array
      updateSlider(actualStartIndex, false);
      
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
      modal.addEventListener('remove', () => {
        document.body.style.overflow = '';
      });
    }

    /**
     * Handles debounced 'resize' events on the window.
     */
    handleResize() {
      // Reset distance from leading edge of one slide to the next.
      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;

      if (this.thumbs && this.currentThumb) {
        this.checkThumbVisibilty(this.currentThumb);
      }
    }

    /**
     * Stub for variant-picker calls. Listening to change event instead.
     */
    // eslint-disable-next-line class-methods-use-this
    setActiveMedia() {}

    /**
     * Sets the active media item.
     * @param {Element} mediaItem - Media element to set as active.
     * @param {boolean} [scrollToItem=true] - Scroll to the active media item.
     */
    customSetActiveMedia(mediaItem, scrollToItem = true) {
      if (mediaItem === this.currentItem) return;
      window.pauseAllMedia(this);
      this.currentItem = mediaItem;
      this.currentIndex = this.visibleItems.indexOf(this.currentItem);

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        // Update the active class and scroll to the active media
        if (this.stackedUnderline) {
          if (this.previousMediaItem) this.previousMediaItem.classList.remove('is-active');
          mediaItem.classList.add('is-active');
          this.previousMediaItem = mediaItem;
        }

        if (this.stackedScroll !== 'never') {
          const y = mediaItem.getBoundingClientRect().top
            + document.documentElement.scrollTop - 150;

          // If the element is far enough away to scroll to it
          if (Math.abs(y - document.documentElement.scrollTop) > 300) {
            window.scrollTo({
              top: y < 100 ? 0 : y,
              behavior: 'smooth'
            });
          }
        }
        return;
      }

      if (scrollToItem) this.viewer.scrollTo({ left: this.currentItem.offsetLeft });
      if (this.thumbs) this.setActiveThumb();

      if (this.controls) {
        if (this.prevBtn) {
          this.prevBtn.disabled = this.currentIndex === 0;
        }

        if (this.nextBtn) {
          this.nextBtn.disabled = this.currentIndex === this.visibleItems.length - 1;
        }

        if (this.counterCurrent) {
          this.counterCurrent.textContent = this.currentIndex + 1;
        }
      }

      this.announceLiveRegion(this.currentItem, this.currentIndex + 1);

      if (this.xrButton && mediaItem.dataset.mediaType === 'model') {
        this.xrButton.dataset.shopifyModel3dId = mediaItem.dataset.mediaId;
      }
    }

    /**
     * Sets the active thumbnail.
     */
    setActiveThumb() {
      this.currentThumb = this.thumbs.querySelector(
        `[data-media-id="${this.currentItem.dataset.mediaId}"]`
      );
      const btn = this.currentThumb.querySelector('button');

      this.thumbs.querySelectorAll('.media-thumbs__btn').forEach((el) => {
        el.classList.remove('is-active');
        el.removeAttribute('aria-current');
      });

      btn.classList.add('is-active');
      btn.setAttribute('aria-current', 'true');
      this.checkThumbVisibilty(this.currentThumb);
    }

    /**
     * Creates an array of the visible media items.
     */
    setVisibleItems() {
      this.viewerItems = this.querySelectorAll('.media-viewer__item');
      this.visibleItems = Array.from(this.viewerItems).filter((el) => el.clientWidth > 0);
      if (this.counterTotal) {
        this.counterTotal.textContent = this.visibleItems.length;
      }
    }

    /**
     * Ensures a thumbnail is in the visible area of the slider.
     * @param {Element} thumb - Thumb item element.
     */
    checkThumbVisibilty(thumb) {
      const scrollPos = this.thumbs.scrollLeft;
      const lastVisibleThumbOffset = this.thumbs.clientWidth + scrollPos;
      const thumbOffset = thumb.offsetLeft;

      if (thumbOffset + thumb.clientWidth > lastVisibleThumbOffset || thumbOffset < scrollPos) {
        this.thumbs.scrollTo({ left: thumbOffset, behavior: 'smooth' });
      }
    }

    /**
     * Updates the media gallery status.
     * @param {Element} mediaItem - Active media element.
     * @param {number} index - Active media index.
     */
    announceLiveRegion(mediaItem, index) {
      const image = mediaItem.querySelector('.media-viewer img');
      if (!image) return;

      this.liveRegion.setAttribute('aria-hidden', 'false');
      this.liveRegion.innerHTML = theme.strings.imageAvailable.replace('[index]', index);

      setTimeout(() => {
        this.liveRegion.setAttribute('aria-hidden', 'true');
      }, 2000);
    }

    /**
     * Loads the deferred media for the active item.
     * @param {Element} mediaItem - Active media element.
     */
    static playActiveMedia(mediaItem) {
      window.pauseAllMedia();
      const deferredMedia = mediaItem.querySelector('deferred-media');
      if (deferredMedia) deferredMedia.loadContent();
    }
  }

  customElements.define('media-gallery', MediaGallery);
}
